import { fabric } from "fabric"
import GroupObject from "../objects/group"
import Calc from "@/common/util/calculator"
import BaseFixmethod from "@/signomatic/app/fixmethods/base"
import CircleObject from "@/signomatic/app/objects/circle"

export default class SuctionCupsFixmethod extends BaseFixmethod {
  private cupDiameter = Calc.mmToPx(30)
  private cupHoleDiameter = Calc.mmToPx(9.4)

  get externalTop() {
    return this.sides.top
  }

  get externalSides() {
    return this.sides.sides
  }

  get externalGap() {
    return this.externalTop ? 30 / 2 : 30
  }

  get externalTopPadding() {
    return this.externalTop ? this.cupDiameter / 2 : 0
  }

  get externalLeftPadding(): number {
    return this.externalSides ? this.cupDiameter / 2 : 0
  }

  get externalRightPadding(): number {
    return this.externalSides ? this.cupDiameter / 2 : 0
  }

  private get sides() {
    const { shape, width, height } = this.state
    const def = {
      top: false,
      sides: false,
      corners: false,
    }

    if ((shape === "rect" || shape === "round") && width < 150 && height >= 200) {
      return { ...def, corners: true }
    } else if (shape === "ellipse" || shape === "thorny") {
      return { ...def, corners: true }
    } else if (shape === "triangle" || shape == "rotated_rect") {
      return { ...def, corners: true }
    } else if (["rounded_top", "rounded_sides", "curved_top", "arched_top"].includes(shape)) {
      return { ...def, corners: true }
    } else if (width < 150 && height < 200) {
      return { ...def, top: true }
    } else if (width >= 150 && height < 200) {
      return { ...def, sides: true }
    } else if (width < 150 && height >= 200) {
      return { ...def, sides: true }
    } else if (width >= 150 && height >= 200) {
      return { ...def, corners: true }
    }

    return def
  }

  topDrawable() {
    const { widthPx, heightPx } = this.state
    const holeRadius = this.cupHoleDiameter / 2
    const items: fabric.Object[] = []

    if (this.sides.top) {
      this.drawCup(items, 0, 0, holeRadius, true)
    } else if (this.sides.sides) {
      this.drawCup(items, 0, heightPx / 2, holeRadius)
      this.drawCup(items, widthPx, heightPx / 2, holeRadius)
    } else if (this.sides.corners) {
      const positions = this.positions()
      for (const e of positions) {
        this.drawCup(items, e.left, e.top, e.diameter / 2, positions.length === 1, e.stretch)
      }
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: this.state.widthPx,
        height: this.state.heightPx,
      }),
    ]
  }

  engravingDrawable() {
    const { widthPx, heightPx } = this.state
    const holeRadius = this.cupHoleDiameter / 2
    const items: fabric.Object[] = []

    if (this.sides.top) {
      this.drawEngravingCup(items, 0, 0, holeRadius, true)
    } else if (this.sides.sides) {
      this.drawEngravingCup(items, 0, heightPx / 2, holeRadius)
      this.drawEngravingCup(items, widthPx, heightPx / 2, holeRadius)
    } else if (this.sides.corners) {
      const positions = this.positions()
      for (const e of positions) {
        if (e.stretch) {
          e.diameter = this.cupHoleDiameter
        }

        this.drawEngravingCup(items, e.left, e.top, e.diameter / 2, positions.length === 1, e.stretch)
      }
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: this.state.widthPx,
        height: this.state.heightPx,
      }),
    ]
  }

  autoSizePadding(state: SignState) {
    const { widthPx, height, heightPx, shape } = state
    const fourScrews = height >= 100
    const extra = fourScrews && shape === "round" ? Math.min(widthPx, heightPx) * 0.1 : 0

    if (this.sides.corners) {
      return this.cupDiameter + extra
    }

    if (this.sides.sides || this.sides.top) {
      return this.cupDiameter * 0.75
    }

    return 0
  }

  private positions() {
    const { shape, width, widthPx, height, heightPx } = this.state
    const hasBorder = this.drawables.hasBorder
    const borderWidth = this.drawables.borderWidth
    const positions = []
    const defaults = { diameter: this.cupHoleDiameter }

    if (this.sides.corners) {
      if (shape === "rect" || shape === "round") {
        if (width <= 149 && height <= 200) {
          const distance = Calc.mmToPx(hasBorder ? 8 : 16)
          const center = widthPx / 2

          positions.push({
            left: center,
            top: borderWidth + distance,
            diameter: this.cupHoleDiameter,
          })
          positions.push({
            left: center,
            top: heightPx - borderWidth - distance,
            diameter: this.cupHoleDiameter,
          })
        } else {
          let min = 0
          let max = 0

          if (shape === "rect") {
            min = Calc.mmToPx(hasBorder ? 7 : 16)
            max = Calc.mmToPx(hasBorder ? 12 : 24)
          } else if (shape === "round") {
            min = Calc.mmToPx(hasBorder ? 8 : 17)
            max = Calc.mmToPx(hasBorder ? 12 : 24)
          }

          const distance = this.drawables.sizeRelative("longest", min, max, {
            minWidth: 149,
            minHeight: 200,
          })

          const left = borderWidth + distance
          const top = borderWidth + distance
          const right = widthPx - borderWidth - distance
          const bottom = heightPx - borderWidth - distance

          positions.push({ left, top, diameter: this.cupHoleDiameter })
          positions.push({ left: right, top, diameter: this.cupHoleDiameter })
          positions.push({ left, top: bottom, diameter: this.cupHoleDiameter })
          positions.push({ left: right, top: bottom, diameter: this.cupHoleDiameter })
        }
      } else if (shape === "rotated_rect") {
        const distance = this.drawables.sizeRelative("width", Calc.mmToPx(18), Calc.mmToPx(26))

        const center = widthPx / 2
        const left = distance
        const right = widthPx - distance

        positions.push({ left, top: center, diameter: this.cupHoleDiameter })
        positions.push({ left: right, top: center, diameter: this.cupHoleDiameter })
      } else if (shape === "ellipse") {
        if (width < 150) {
          const distance = Calc.mmToPx(hasBorder ? 7.5 : 9)

          positions.push({
            left: widthPx / 2,
            top: borderWidth + distance,
            diameter: this.cupHoleDiameter,
          })
        } else {
          const distance = Calc.mmToPx(hasBorder ? 5.5 : 9)

          const left = borderWidth + distance
          const right = widthPx - borderWidth - distance
          const top = heightPx / 2

          positions.push({ left, top, diameter: this.cupHoleDiameter })
          positions.push({ left: right, top, diameter: this.cupHoleDiameter })
        }
      } else if (shape === "thorny") {
        if (width < 150) {
          const min = Calc.mmToPx(hasBorder ? 5.5 : 9)
          const max = Calc.mmToPx(hasBorder ? 10 : 16)
          const distance = this.drawables.sizeRelative("width", min, max, {
            maxWidth: 149,
          })

          positions.push({
            left: widthPx / 2,
            top: borderWidth + distance,
            diameter: this.cupHoleDiameter,
          })
        } else {
          const min = Calc.mmToPx(hasBorder ? 5.5 : 16.5)
          const max = Calc.mmToPx(hasBorder ? 10 : 38)

          const thornDistance = this.thornDistance
          const thornExtra = hasBorder ? thornDistance : 0
          const distance = this.drawables.sizeRelative("width", min, max, {
            minWidth: 150,
          })

          positions.push({
            left: thornExtra + borderWidth + distance,
            top: heightPx / 2,
            diameter: this.cupHoleDiameter,
          })
          positions.push({
            left: widthPx - thornExtra - borderWidth - distance,
            top: heightPx / 2,
            diameter: this.cupHoleDiameter,
          })
        }
      } else if (shape === "triangle") {
        const min = Calc.mmToPx(8.5)
        const max = Calc.mmToPx(12)
        const distance = this.drawables.sizeRelative("height", min, max, {
          minHeight: 100,
          maxHeight: 295,
        })

        const offset = Calc.triangleHorizontalOffset(widthPx, heightPx, distance)
        const minTop = Calc.mmToPx(20)
        const maxTop = Calc.mmToPx(22)
        const distanceTop = this.drawables.sizeRelative("height", minTop, maxTop, {
          minHeight: Calc.mmToPx(100),
          maxHeight: Calc.mmToPx(295),
        })

        const center = widthPx / 2
        const top = distanceTop
        const topStretch = distance
        const bottom = heightPx - distance
        const left = offset + distance
        const right = widthPx - offset - distance

        positions.push({ ...defaults, left: center, top })
        positions.push({ ...defaults, left, top: bottom })
        positions.push({ ...defaults, left: right, top: bottom })
        positions.push({
          diameter: this.cupDiameter,
          left: center,
          top: topStretch,
          stretch: true,
        })
      } else if (["rounded_top", "rounded_sides", "curved_top", "arched_top"].includes(shape)) {
        const oneCup = shape == "arched_top" && width < 100
        const fourCups = (width > 149 && shape != "arched_top") || (shape == "arched_top" && width > 199)
        const twoCups = !fourCups && !oneCup

        let distanceSide = 0
        let distanceTop = 0
        const relOptions = { minWidth: 150, minHeight: 150 }

        if (fourCups) {
          if (shape == "arched_top") {
            distanceSide = Calc.mmToPx(12)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(80), Calc.mmToPx(115), { minHeight: 200 })
          } else if (shape == "rounded_top") {
            distanceSide = Calc.mmToPx(10)
            distanceTop = this.drawables.sizeRelative("height", Calc.mmToPx(28), Calc.mmToPx(38), relOptions)
          } else if (shape == "rounded_sides") {
            distanceSide = this.drawables.sizeRelative("width", Calc.mmToPx(28), Calc.mmToPx(38), relOptions)
            distanceTop = Calc.mmToPx(10)
          } else if (shape == "curved_top") {
            const distance = this.drawables.sizeRelative("width", Calc.mmToPx(10), Calc.mmToPx(12), relOptions)
            const curveTop = this.drawables.sizeRelative("height", Calc.mmToPx(45), Calc.mmToPx(122), {
              minHeight: 100,
            })

            const left = distance - this.cupDiameter / 2
            const top = distance - this.cupDiameter / 2
            const right = widthPx - distance - this.cupDiameter / 2
            const bottom = heightPx - Calc.mmToPx(10) - this.cupDiameter / 2
            const curve = curveTop - this.cupDiameter / 2

            positions.push({ ...defaults, left, top })
            positions.push({ ...defaults, left, top: bottom })
            positions.push({ ...defaults, left: right, top: curve })
            positions.push({ ...defaults, left: right, top: bottom })

            return positions
          }
        } else {
          distanceTop = Calc.mmToPx(9)
          distanceSide = distanceTop
        }

        const left = distanceSide - this.cupHoleDiameter / 2
        let top = distanceTop - this.cupHoleDiameter / 2
        let bottom = heightPx - distanceTop - this.cupHoleDiameter / 2
        const right = widthPx - distanceSide - this.cupHoleDiameter / 2
        let centerV = heightPx / 2 - this.cupHoleDiameter / 2
        const centerH = widthPx / 2 - this.cupHoleDiameter / 2

        if (shape == "arched_top" && fourCups) {
          top = distanceTop - this.cupHoleDiameter / 2
          bottom = heightPx - distanceSide - this.cupHoleDiameter / 2
          positions.push({
            diameter: this.cupHoleDiameter,
            left: widthPx / 2 - this.cupHoleDiameter / 2,
            top: -this.cupHoleDiameter / 2,
            stretch: true,
          })
        } else if (shape == "arched_top" && twoCups) {
          // arcH constant also in arched_top
          const arcH = heightPx * 0.35
          const squareHeight = heightPx - arcH
          centerV = arcH + squareHeight / 2 - this.cupHoleDiameter / 2
          positions.push({
            ...defaults,
            left: widthPx / 2,
            top: heightPx - centerV - this.cupHoleDiameter * 2,
            stretch: true,
          })
        } else if (shape == "arched_top" && oneCup) {
          top = distanceTop + this.cupHoleDiameter / 2
        }

        if (fourCups) {
          positions.push({ ...defaults, left, top })
          positions.push({ ...defaults, left, top: bottom })
          positions.push({ ...defaults, left: right, top })
          positions.push({ ...defaults, left: right, top: bottom })
        } else if (oneCup) {
          positions.push({ ...defaults, left: centerH, top })
        } else {
          positions.push({ ...defaults, left, top: centerV })
          positions.push({ ...defaults, left: right, top: centerV })
        }

        return positions
      }
    }

    return positions
  }

  private drawCup(
    items: fabric.Object[],
    left: number,
    top: number,
    radius: number,
    counter?: boolean,
    stretch?: boolean
  ) {
    const ringStroke = "#878787"
    const outerFill = "#DADADA"
    const insideFill = "#EDEDED"

    if (stretch) {
      items.push(
        new fabric.Circle({
          radius,
          left,
          top,
          stroke: "transparent",
          strokeWidth: 1,
          fill: "transparent",
          originX: "center",
          originY: "center",
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
        })
      )

      return
    }

    // DECORATIVE RINGS

    // Second ring from hole
    items.push(
      new CircleObject({
        left,
        top,
        radius: radius * 3,
        stroke: ringStroke,
        strokeWidth: 1,
        fill: outerFill,
        originX: "center",
        originY: "center",
      })
    )

    // First ring from hole
    items.push(
      new CircleObject({
        left,
        top,
        radius: radius * 1.4,
        stroke: ringStroke,
        strokeWidth: 1,
        fill: outerFill,
        originX: "center",
        originY: "center",
      })
    )

    // Actual hole
    items.push(
      new CircleObject({
        radius,
        left,
        top,
        stroke: "#aaa",
        strokeWidth: 1,
        fill: insideFill,
        originX: "center",
        originY: "center",
        startAngle: 0,
        endAngle: Calc.toDeg(Math.PI * 2),
      }).addClass("hang", "suction_cups")
    )

    // Ring inside hole
    items.push(
      new CircleObject({
        left,
        top,
        radius: radius / 1.5,
        stroke: ringStroke,
        strokeWidth: 1,
        fill: insideFill,
        originX: "center",
        originY: "center",
      })
    )

    // Counterweight
    if (counter === true) {
      items.push(
        new CircleObject({
          radius: radius * 3,
          left,
          top: this.state.heightPx - top,
          stroke: "transparent",
          strokeWidth: 1,
          fill: "transparent",
          originX: "center",
          originY: "center",
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
        })
      )
    }
  }

  private drawEngravingCup(
    items: fabric.Object[],
    left: number,
    top: number,
    radius: number,
    counter?: boolean,
    stretch?: boolean
  ) {
    if (stretch) {
      items.push(
        new fabric.Circle({
          radius,
          left,
          top,
          stroke: "transparent",
          strokeWidth: this.drawables.engravingStrokeWidth,
          fill: "transparent",
          originX: "center",
          originY: "center",
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
        })
      )

      return
    }

    // Actual hole
    items.push(
      new CircleObject({
        radius,
        left,
        top,
        stroke: this.drawables.engravingColor,
        strokeWidth: this.drawables.engravingStrokeWidth,
        fill: "transparent",
        originX: "center",
        originY: "center",
        startAngle: 0,
        endAngle: Calc.toDeg(Math.PI * 2),
      }).addClass("hang", "suction_cups")
    )

    // Counterweight
    if (counter === true) {
      items.push(
        new CircleObject({
          radius,
          left,
          top: this.state.heightPx - top,
          stroke: "transparent",
          strokeWidth: 1,
          fill: "transparent",
          originX: "center",
          originY: "center",
          startAngle: 0,
          endAngle: Calc.toDeg(Math.PI * 2),
        })
      )
    }
  }
}
